import { useState } from 'react';
import { 
  Plus, Building2, MapPin, Phone, Mail, 
  FileText, CheckCircle, AlertCircle, RefreshCw,
  User, Globe, DollarSign, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_TYPES = [
  { value: 'ncc', label: 'NCC / Transfer', icon: '🚗' },
  { value: 'guide', label: 'Guida Turistica', icon: '🎭' },
  { value: 'experience', label: 'Esperienza', icon: '🎯' },
  { value: 'accommodation', label: 'Alloggio', icon: '🏨' },
  { value: 'restaurant', label: 'Ristorante', icon: '🍽️' },
  { value: 'winery', label: 'Cantina', icon: '🍷' },
  { value: 'rental', label: 'Noleggio', icon: '🚴' },
  { value: 'wellness', label: 'Benessere', icon: '💆' },
  { value: 'babysitting', label: 'Babysitter', icon: '👶' },
];

const SUBTYPES = {
  ncc: [
    { value: 'ncc', label: 'NCC Standard' },
    { value: 'transfer', label: 'Transfer Aeroporto' },
  ],
  guide: [
    { value: 'guida_turistica', label: 'Guida Turistica' },
    { value: 'guida_escursionistica', label: 'Guida Escursionistica' },
    { value: 'tour_operator', label: 'Tour Operator' },
  ],
  experience: [
    { value: 'cooking_class', label: 'Cooking Class' },
    { value: 'wine_tasting', label: 'Degustazione Vini' },
    { value: 'tour', label: 'Tour Guidato' },
  ],
  accommodation: [
    { value: 'hotel', label: 'Hotel' },
    { value: 'b&b', label: 'B&B' },
    { value: 'agriturismo', label: 'Agriturismo' },
  ],
  restaurant: [
    { value: 'ristorante', label: 'Ristorante' },
    { value: 'trattoria', label: 'Trattoria' },
  ],
  winery: [
    { value: 'cantina', label: 'Cantina' },
  ],
  rental: [
    { value: 'ebike', label: 'E-Bike' },
    { value: 'bike', label: 'Bici' },
  ],
  wellness: [
    { value: 'spa', label: 'Spa' },
  ],
  babysitting: [
    { value: 'babysitter', label: 'Babysitter' },
  ],
};

const AVAILABILITY_MODES = [
  { value: 'on_request', label: 'Su Richiesta', desc: 'Verifichiamo disponibilità' },
  { value: 'instant', label: 'Prenotazione Diretta', desc: 'Disponibilità confermata' },
  { value: 'calendar', label: 'Calendario', desc: 'Gestisce un calendario' },
];

const PRICE_LEVELS = [
  { value: 'economico', label: '€ Economico' },
  { value: 'medio', label: '€€ Medio' },
  { value: 'alto', label: '€€€ Alto' },
  { value: 'luxury', label: '€€€€ Luxury' },
];

const COMUNI_UMBRIA = [
  'Perugia', 'Assisi', 'Spoleto', 'Orvieto', 'Terni', 'Foligno', 
  'Gubbio', 'Città di Castello', 'Todi', 'Norcia', 'Montefalco',
  'Bevagna', 'Spello', 'Castiglione del Lago', 'Passignano sul Trasimeno',
  'Narni', 'Trevi', 'Umbertide', 'Bastia Umbra', 'Deruta', 'Marsciano',
  'Panicale', 'Città della Pieve', 'Magione', 'Corciano'
];

export function QuickAddPanel() {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    subtype: '',
    comune: '',
    macro_area: '',
    contact_phone: '',
    contact_email: '',
    description: '',
    availability_mode: 'on_request',
    supplier_type: '',
    price_level: '',
    languages: [],
    notes: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [recentlyAdded, setRecentlyAdded] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Reset subtype when type changes
      ...(field === 'type' ? { subtype: '' } : {})
    }));
  };

  const toggleLanguage = (lang) => {
    setSelectedLanguages(prev => 
      prev.includes(lang) 
        ? prev.filter(l => l !== lang)
        : [...prev, lang]
    );
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      subtype: '',
      comune: '',
      macro_area: '',
      contact_phone: '',
      contact_email: '',
      description: '',
      availability_mode: 'on_request',
      supplier_type: '',
      price_level: '',
      languages: [],
      notes: '',
    });
    setSelectedLanguages([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.type || !formData.comune) {
      toast.error('Compila i campi obbligatori: Nome, Tipo, Comune');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        languages: selectedLanguages,
      };

      const res = await fetch(`${API}/admin/entities/quick-add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Errore durante la creazione');
      }

      const data = await res.json();
      
      toast.success(`Provider "${data.name}" creato con successo!`);
      
      // Add to recently added
      setRecentlyAdded(prev => [data, ...prev.slice(0, 4)]);
      
      // Reset form
      resetForm();
      
    } catch (err) {
      console.error('Quick add failed:', err);
      toast.error(err.message || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  const currentSubtypes = SUBTYPES[formData.type] || [];

  return (
    <div className="space-y-6" data-testid="quick-add-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-green-600" />
            Quick Add Provider
          </CardTitle>
          <p className="text-sm text-gray-500">
            Aggiungi rapidamente un fornitore senza passare dall'onboarding completo
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Nome Attività <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="Es: Transfer Umbria di Mario Rossi"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  data-testid="quick-add-name"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Tipo <span className="text-red-500">*</span>
                </label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => handleChange('type', v)}
                >
                  <SelectTrigger data-testid="quick-add-type">
                    <SelectValue placeholder="Seleziona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDER_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          <span>{type.icon}</span>
                          {type.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Subtype */}
            {currentSubtypes.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Sottotipo
                </label>
                <Select 
                  value={formData.subtype} 
                  onValueChange={(v) => handleChange('subtype', v)}
                >
                  <SelectTrigger data-testid="quick-add-subtype">
                    <SelectValue placeholder="Seleziona sottotipo (opzionale)" />
                  </SelectTrigger>
                  <SelectContent>
                    {currentSubtypes.map(sub => (
                      <SelectItem key={sub.value} value={sub.value}>
                        {sub.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Comune <span className="text-red-500">*</span>
                </label>
                <Select 
                  value={formData.comune} 
                  onValueChange={(v) => handleChange('comune', v)}
                >
                  <SelectTrigger data-testid="quick-add-comune">
                    <SelectValue placeholder="Seleziona comune" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMUNI_UMBRIA.map(comune => (
                      <SelectItem key={comune} value={comune}>
                        {comune}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Macro Area (opzionale)
                </label>
                <Input
                  placeholder="Verrà dedotta dal comune"
                  value={formData.macro_area}
                  onChange={(e) => handleChange('macro_area', e.target.value)}
                />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  <Phone className="h-4 w-4 inline mr-1" />
                  Telefono
                </label>
                <Input
                  type="tel"
                  placeholder="+39 333 1234567"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  data-testid="quick-add-phone"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Email
                </label>
                <Input
                  type="email"
                  placeholder="info@esempio.it"
                  value={formData.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  data-testid="quick-add-email"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                <FileText className="h-4 w-4 inline mr-1" />
                Descrizione
              </label>
              <Textarea
                placeholder="Breve descrizione del servizio offerto..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                data-testid="quick-add-description"
              />
            </div>

            {/* Business Config */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Modalità Disponibilità
                </label>
                <Select 
                  value={formData.availability_mode} 
                  onValueChange={(v) => handleChange('availability_mode', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABILITY_MODES.map(mode => (
                      <SelectItem key={mode.value} value={mode.value}>
                        <div>
                          <div>{mode.label}</div>
                          <div className="text-xs text-gray-500">{mode.desc}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  <DollarSign className="h-4 w-4 inline mr-1" />
                  Fascia Prezzo
                </label>
                <Select 
                  value={formData.price_level} 
                  onValueChange={(v) => handleChange('price_level', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona fascia" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICE_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Languages */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                <Globe className="h-4 w-4 inline mr-1" />
                Lingue Parlate
              </label>
              <div className="flex flex-wrap gap-2">
                {['Italiano', 'English', 'Français', 'Deutsch', 'Español'].map(lang => (
                  <Button
                    key={lang}
                    type="button"
                    variant={selectedLanguages.includes(lang) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleLanguage(lang)}
                  >
                    {lang}
                  </Button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Note Interne
              </label>
              <Textarea
                placeholder="Note visibili solo all'admin..."
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={2}
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4 border-t">
              <Button 
                type="submit" 
                disabled={loading}
                className="gap-2"
                data-testid="quick-add-submit"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {loading ? 'Creazione...' : 'Crea Provider'}
              </Button>
              <Button 
                type="button" 
                variant="outline"
                onClick={resetForm}
              >
                Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Recently Added */}
      {recentlyAdded.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Aggiunti di Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentlyAdded.map((provider, idx) => (
                <div 
                  key={provider.id || idx}
                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-umbria-charcoal">{provider.name}</p>
                      <p className="text-xs text-gray-500">
                        {provider.type} • {provider.comune}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">{provider.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
